import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect, MatOption } from '@angular/material/select';
import { DivisionByClientContact } from '@models/dashboard/model';
import { Contact } from '@models/profile/model';
import { Observable, of, Subscription } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs';
import { Categories } from 'src/app/shared/constants/analyticsCategory.constant';
import { BrandService } from 'src/app/shared/services/brand.service';
import { ErrorNotificationService } from 'src/app/shared/services/error-notification.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ContactQuery, CurrentUserQuery } from 'src/app/state';

import { AuthenticationService } from '../auth/services/authentication.service';
import { CommonConstants } from './../../shared/constants/common.constant';
import { DashboardService } from '@app/shared/services/dashboard.service';
import { RaygunService } from '@app/raygun/raygun.service';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-division-select',
  templateUrl: './division-select.component.html',
  styleUrls: ['./division-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatOption, MatSelectSearchComponent, MatButton, MatProgressSpinner, AsyncPipe],
})
export class DivisionSelectComponent implements OnInit, OnDestroy {
  private authenticationService = inject(AuthenticationService);
  private dashboardService = inject(DashboardService);
  private contactQuery = inject(ContactQuery);
  public renderer = inject(Renderer2);
  public dialog = inject(MatDialog);
  private sharedService = inject(SharedService);
  private errorNotificationService = inject(ErrorNotificationService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private brandService = inject(BrandService);
  private utilityService = inject(UtilityService);
  private currentUserQuery = inject(CurrentUserQuery);
  private raygunService = inject(RaygunService);

  public loading: boolean;
  public contactName: string;
  public numberOfDivisions: number;
  public contacts$: Observable<Contact[]>;
  public allDivisions: DivisionByClientContact[];
  public allDivisions$: Observable<DivisionByClientContact[]>;

  /** control for the selected division */
  public selectedDivisionCtrl: FormControl = new FormControl('', [Validators.required]);

  /** control for the MatSelect filter keyword */
  public divisionFilterCtrl: FormControl = new FormControl();

  /** list of divisions filtered by search keyword */
  public filteredDivisions: DivisionByClientContact[] = [];
  public readonly brand = this.brandService.getBrandResources();

  @ViewChild('singleSelect') public singleSelect: MatSelect;
  private subscriptions = new Subscription();

  public ngOnInit(): void {
    this.subscriptions.add(
      this.errorNotificationService.errorNotification.subscribe(() => {
        this.loading = false;
        this.utilityService.showGrowelMessage(this.sharedService.getMessageByBrand(CommonConstants.errorCodes.apiFailure), false);
      })
    );

    this.contacts$ = this.contactQuery.selectAllWhenLoaded().pipe(
      tap((contacts) => {
        if (contacts.length) {
          const currentContact = contacts.find((contact) => contact.email.toLowerCase() === this.currentUserQuery.getEmailAddress());
          this.contactName = `${currentContact.firstName} ${currentContact.lastName}`;
        }
      })
    );

    this.allDivisions$ = (
      this.authenticationService.selectedClientSignInTemp // this value will be set if we came through the distributor component before this
        ? of(this.authenticationService.selectedClientSignInTemp)
        : this.dashboardService.loadClientsByContact().pipe(
          first(),
          map((clients) => {
            if (clients.length !== 1) {
              this.raygunService.logError(`expected exactly 1 client record, found ${clients.length}`);
              return undefined;
            }
            return clients[0].clientId;
          })
        )
    ).pipe(
      switchMap((clientId) =>
        this.dashboardService.loadDivisionsByClientContact(clientId).pipe(
          first(),
          tap((divisions) => {
            this.numberOfDivisions = divisions.length;
            this.allDivisions = divisions;
            /** load the initial divisions list */
            this.filteredDivisions = this.allDivisions;
          })
        )
      )
    );

    /** listen for search field value changes */
    this.subscriptions.add(this.divisionFilterCtrl.valueChanges.subscribe(() => this.filterDivisions()));
  }

  /**
   * This method is trigerred when user selects the division and clicks on continue.
   * This calls the token service to get the new token based on the selected division.
   * @returns {void}
   * @memberof DistributorComponent
   */
  public async onDivisionSelect(): Promise<void> {
    await this.authenticateAndRedirectUser();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private async authenticateAndRedirectUser(): Promise<void> {
    this.loading = true;
    this.googleAnalyticsService.eventEmitter(Categories.EventCategories.SelectDivision);
    const divisionId = this.selectedDivisionCtrl.value.divisionId as string;
    await this.authenticationService.signInWithStoredCredentials({ divisionId }, true, { keepCredentials: true });
    this.loading = false;
  }

  private filterDivisions(): void {
    // get the search keyword
    let search = this.divisionFilterCtrl.value;
    if (!search) {
      this.filteredDivisions = this.allDivisions;
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter divisions
    this.filteredDivisions = this.allDivisions.filter((dbc) => dbc.divisionName.toLowerCase().indexOf(search) > -1);
  }
}
